<template>
  <div
    :class="classes"
    @mouseenter="handleMouseenter"
    @mouseleave="handleMouseleave"
    v-click-outside="handleClose">
    <div
      :class="[prefixCls + '-rel']"
      ref="reference"
      @click="handleClick"
      @mousedown="handleFocus(false)"
      @mouseup="handleBlur(false)">
      <slot></slot>
    </div>
    <transition name="fade">
      <div
        :class="popperClasses"
        :style="styles"
        ref="popper"
        v-show="visible"
        @click="handleTransferClick"
        @mouseenter="handleMouseenter"
        @mouseleave="handleMouseleave"
        :data-transfer="transfer"
        v-transfer-dom>
        <div :class="[prefixCls + '-content']">
          <div :class="[prefixCls + '-arrow']"></div>
          <div :class="[prefixCls + '-inner']" v-if="confirm">
            <div :class="[prefixCls + '-body']">
              <i class="ivu-icon ivu-icon-ios-help-circle"></i>
              <div :class="[prefixCls + '-body-message']"><slot name="title">{{ title }}</slot></div>
            </div>
            <div :class="[prefixCls + '-footer']">
              <i-button type="text" size="small" @click.native="cancel">{{ localeCancelText }}</i-button>
              <i-button type="primary" size="small" @click.native="ok">{{ localeOkText }}</i-button>
            </div>
          </div>
          <div :class="[prefixCls + '-inner']" v-if="!confirm">
            <div :class="[prefixCls + '-title']" :style="contentPaddingStyle" v-if="showTitle" ref="title"><slot name="title"><div :class="[prefixCls + '-title-inner']">{{ title }}</div></slot></div>
            <div :class="[prefixCls + '-body']" :style="contentPaddingStyle">
              <div :class="contentClasses"><slot name="content"><div :class="[prefixCls + '-body-content-inner']">{{ content }}</div></slot></div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>
<script>
  import Popper from './popper';
  import {directive as clickOutside} from 'v-click-outside-x';
  import TransferDom from '../../directives/transfer-dom';
  import { oneOf } from './../assist';

  const prefixCls = 'am-poptip';

  export default {
    name: 'Poptip',
    mixins: [ Popper ],
    directives: { clickOutside, TransferDom },
    components: { },
    props: {
      trigger: {
        validator (value) {
          return oneOf(value, ['click', 'focus', 'hover']);
        },
        default: 'click'
      },
      placement: {
        validator (value) {
          return oneOf(value, ['top', 'top-start', 'top-end', 'bottom', 'bottom-start', 'bottom-end', 'left', 'left-start', 'left-end', 'right', 'right-start', 'right-end']);
        },
        default: 'top'
      },
      title: {
        type: [String, Number]
      },
      content: {
        type: [String, Number],
        default: ''
      },
      width: {
        type: [String, Number]
      },
      confirm: {
        type: Boolean,
        default: false
      },
      okText: {
        type: String
      },
      cancelText: {
        type: String
      },
      transfer: {
        type: Boolean,
        default () {
          return !this.$IVIEW || this.$IVIEW.transfer === '' ? false : this.$IVIEW.transfer;
        }
      },
      popperClass: {
        type: String
      },
      wordWrap: {
        type: Boolean,
        default: false
      },
      // default by css: 8px 16px
      padding: {
        type: String
      }
    },
    data () {
      return {
        prefixCls: prefixCls,
        showTitle: true,
        isInput: false,
        disableCloseUnderTransfer: false,  // transfer 模式下，点击 slot 也会触发关闭
      };
    },
    computed: {
      classes () {
        return [
          `${prefixCls}`,
          {
            [`${prefixCls}-confirm`]: this.confirm
          }
        ];
      },
      popperClasses () {
        return [
          `${prefixCls}-popper`,
          {
            [`${prefixCls}-confirm`]: this.transfer && this.confirm,
            [`${this.popperClass}`]: !!this.popperClass
          }
        ];
      },
      styles () {
        let style = {};

        if (this.width) {
          style.width = `${this.width}px`;
        }
        return style;
      },
      localeOkText () {
        if (this.okText === undefined) {
          return ('i.poptip.okText');
        } else {
          return this.okText;
        }
      },
      localeCancelText () {
        if (this.cancelText === undefined) {
          return ('i.poptip.cancelText');
        } else {
          return this.cancelText;
        }
      },
      contentClasses () {
        return [
          `${prefixCls}-body-content`,
          {
            [`${prefixCls}-body-content-word-wrap`]: this.wordWrap
          }
        ];
      },
      contentPaddingStyle () {
        const styles = {};
        if (this.padding !== '') styles['padding'] = this.padding;
        return styles;
      }
    },
    methods: {
      handleClick () {
        if (this.confirm) {
          this.visible = !this.visible;
          return true;
        }
        if (this.trigger !== 'click') {
          return false;
        }
        this.visible = !this.visible;
      },
      handleTransferClick () {
        if (this.transfer) this.disableCloseUnderTransfer = true;
      },
      handleClose () {
        if (this.disableCloseUnderTransfer) {
          this.disableCloseUnderTransfer = false;
          return false;
        }
        if (this.confirm) {
          this.visible = false;
          return true;
        }
        if (this.trigger !== 'click') {
          return false;
        }
        this.visible = false;
      },
      handleFocus (fromInput = true) {
        if (this.trigger !== 'focus' || this.confirm || (this.isInput && !fromInput)) {
          return false;
        }
        this.visible = true;
      },
      handleBlur (fromInput = true) {
        if (this.trigger !== 'focus' || this.confirm || (this.isInput && !fromInput)) {
          return false;
        }
        this.visible = false;
      },
      handleMouseenter () {
        if (this.trigger !== 'hover' || this.confirm) {
          return false;
        }
        if (this.enterTimer) clearTimeout(this.enterTimer);
        this.enterTimer = setTimeout(() => {
          this.visible = true;
        }, 100);
      },
      handleMouseleave () {
        if (this.trigger !== 'hover' || this.confirm) {
          return false;
        }
        if (this.enterTimer) {
          clearTimeout(this.enterTimer);
          this.enterTimer = setTimeout(() => {
            this.visible = false;
          }, 100);
        }
      },
      cancel () {
        this.visible = false;
        this.$emit('on-cancel');
      },
      ok () {
        this.visible = false;
        this.$emit('on-ok');
      },
      getInputChildren () {
        const $input = this.$refs.reference.querySelectorAll('input');
        const $textarea = this.$refs.reference.querySelectorAll('textarea');
        let $children = null;

        if ($input.length) {
          $children = $input[0];
        } else if ($textarea.length) {
          $children = $textarea[0];
        }

        return $children;
      }
    },
    mounted () {
      if (!this.confirm) {
//                this.showTitle = this.$refs.title.innerHTML != `<div class="${prefixCls}-title-inner"></div>`;
        this.showTitle = (this.$slots.title !== undefined) || this.title;
      }
      // if trigger and children is input or textarea,listen focus & blur event
      if (this.trigger === 'focus') {
        this.$nextTick(() => {
          const $children = this.getInputChildren();
          if ($children) {
            this.isInput = true;
            $children.addEventListener('focus', this.handleFocus, false);
            $children.addEventListener('blur', this.handleBlur, false);
          }
        });
      }
    },
    beforeDestroy () {
      const $children = this.getInputChildren();
      if ($children) {
        $children.removeEventListener('focus', this.handleFocus, false);
        $children.removeEventListener('blur', this.handleBlur, false);
      }
    }
  };
</script>
<style lang="less" >
  @css-prefix: 'am-';
  @poptip-prefix-cls: ~"@{css-prefix}poptip";
  @poptip-arrow: ~"@{poptip-prefix-cls}-arrow";
  @poptip-max-width: 250px;
  @poptip-arrow-width: 7px;
  @poptip-arrow-outer-width: (@poptip-arrow-width + 1);
  @poptip-distance: @poptip-arrow-width - 1 + 4;
  //@poptip-arrow-color: fadein(@border-color-base, 5%);
  @poptip-arrow-color: hsla(0,0%,85%,.5);
  // Border color
  @border-color-base      : #dcdee2;  // outside
  @border-color-split     : #e8eaec;  // inside
  //Base
  @font-size-base         : 14px;
  @title-color            : #17233d;
  @text-color             : #333;
  @font-size-small        : 12px;
  @line-height-base       : 1.5;
  // Shadow
  @shadow-color           : rgba(0, 0, 0, .2);
  @shadow-base            : @shadow-down;
  @shadow-card            : 0 1px 1px 0 rgba(0,0,0,.1);
  @shadow-up              : 0 -1px 6px @shadow-color;
  @shadow-down            : 0 1px 6px @shadow-color;
  @shadow-left            : -1px 0 6px @shadow-color;
  @shadow-right           : 1px 0 6px @shadow-color;
  //z-index
  @zindex-tooltip         : 1060;
  //Color
  @warning-color          : #ff9900;
  .@{poptip-prefix-cls} {
    display: inline-block;

    &-rel{
      display: inline-block;
      position: relative;
      width:100%;
      vertical-align: middle;
    }

    &-title {
      margin: 0;
      padding: 8px 16px;
      position: relative;

      &:after{
        content: '';
        display: block;
        height: 1px;
        position: absolute;
        left: 8px;
        right: 8px;
        bottom: 0;
        background-color: @border-color-split;
      }

      &-inner{
        color: @title-color;
        font-size: @font-size-base;
      }
    }

    &-body{
      padding: 8px 16px;

      &-content{
        overflow: auto;

        &-word-wrap{
          white-space: pre-wrap;
          text-align: justify;
        }

        &-inner{
          color: @text-color;
        }
      }
    }

    &-inner{
      width: 100%;
      background-color: #fff;
      background-clip: padding-box;
      //border: 1px solid @border-color-split;
      border-radius: 5px;
      box-shadow: @shadow-base;
      white-space: nowrap;
    }

    &-popper{
      min-width: 150px;
      font-size: @font-size-small;
      .popper(@poptip-arrow, @poptip-arrow-width, @poptip-distance, @poptip-arrow-color);

      &[x-placement^="top"] .@{poptip-arrow}:after {
        content: " ";
        bottom: 1px;
        margin-left: -@poptip-arrow-width;
        border-bottom-width: 0;
        border-top-width: @poptip-arrow-width;
        border-top-color: #fff;
      }

      &[x-placement^="right"] .@{poptip-arrow}:after {
        content: " ";
        left: 1px;
        bottom: -@poptip-arrow-width;
        border-left-width: 0;
        border-right-width: @poptip-arrow-width;
        border-right-color: #fff;
      }

      &[x-placement^="bottom"] .@{poptip-arrow}:after {
        content: " ";
        top: 1px;
        margin-left: -@poptip-arrow-width;
        border-top-width: 0;
        border-bottom-width: @poptip-arrow-width;
        border-bottom-color: #fff;
      }

      &[x-placement^="left"] .@{poptip-arrow}:after {
        content: " ";
        right: 1px;
        border-right-width: 0;
        border-left-width: @poptip-arrow-width;
        border-left-color: #fff;
        bottom: -@poptip-arrow-width;
      }
    }

    &-arrow{
      &, &:after{
        display: block;
        width: 0;
        height: 0;
        position: absolute;
        border-color: transparent;
        border-style: solid;
      }
    }
    &-arrow {
      border-width: @poptip-arrow-outer-width;
    }
    &-arrow:after{
      content: "";
      border-width: @poptip-arrow-width;
    }

    &-confirm &-popper{
      max-width: 300px;
    }
    &-confirm &-inner{
      white-space: normal;
    }

    &-confirm &-body{
      padding: 16px 16px 8px;
      .ivu-icon{
        font-size: 16px;
        color: @warning-color;
        line-height: 18px;
        position: absolute;
      }

      &-message{
        padding-left: 20px;
      }
    }

    &-confirm &-footer{
      text-align: right;
      padding: 8px 16px 16px;
      button {
        margin-left: 4px;
      }
    }
  }
  .popper(@arrow, @arrow-width, @arrow-distance, @bg){
    display: block;
    visibility: visible;
    font-size: @font-size-small;
    line-height: @line-height-base;
    position: absolute;
    z-index: @zindex-tooltip;

    &[x-placement^="top"] {
      padding: @arrow-width 0 @arrow-distance 0;
    }
    &[x-placement^="right"] {
      padding: 0 @arrow-width 0 @arrow-distance;
    }
    &[x-placement^="bottom"] {
      padding: @arrow-distance 0 @arrow-width 0;
    }
    &[x-placement^="left"] {
      padding: 0 @arrow-distance 0 @arrow-width;
    }

    &[x-placement^="top"] .@{arrow} {
      bottom: @arrow-distance - @arrow-width;
      border-width: @arrow-width @arrow-width 0;
      border-top-color: @bg;
    }
    &[x-placement="top"] .@{arrow} {
      left: 50%;
      margin-left: -@arrow-width;
    }
    &[x-placement="top-start"] .@{arrow} {
      left: 16px;
    }
    &[x-placement="top-end"] .@{arrow} {
      right: 16px;
    }

    &[x-placement^="right"] .@{arrow} {
      left: @arrow-distance - @arrow-width;
      border-width: @arrow-width @arrow-width @arrow-width 0;
      border-right-color: @bg;
    }
    &[x-placement="right"] .@{arrow} {
      top: 50%;
      margin-top: -@arrow-width;
    }
    &[x-placement="right-start"] .@{arrow} {
      top: 8px;
    }
    &[x-placement="right-end"] .@{arrow} {
      bottom: 8px;
    }

    &[x-placement^="left"] .@{arrow} {
      right: @arrow-distance - @arrow-width;
      border-width: @arrow-width 0 @arrow-width @arrow-width;
      border-left-color: @bg;
    }
    &[x-placement="left"] .@{arrow} {
      top: 50%;
      margin-top: -@arrow-width;
    }
    &[x-placement="left-start"] .@{arrow} {
      top: 8px;
    }
    &[x-placement="left-end"] .@{arrow} {
      bottom: 8px;
    }

    &[x-placement^="bottom"] .@{arrow} {
      top: @arrow-distance - @arrow-width;
      border-width: 0 @arrow-width @arrow-width;
      border-bottom-color: @bg;
    }
    &[x-placement="bottom"] .@{arrow} {
      left: 50%;
      margin-left: -@arrow-width;
    }
    &[x-placement="bottom-start"] .@{arrow} {
      left: 16px;
    }
    &[x-placement="bottom-end"] .@{arrow} {
      right: 16px;
    }
  }
</style>



// WEBPACK FOOTER //
// components/Poptip/Poptip.vue